import instance from "../../Interceptor/api_instance";

import { useEffect, useState } from "react";
import { useMutate } from "../../Hooks/use-mutate";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "../../Components/ui/form";
import { Logo } from "../../Media/logos";

type PasswordPostData = {
  new_password: string;
  confirm_password: string;
};

const validateNewPassword = (password: string): string | null => {
  if (password.length < 8) {
    return "Password must be at least 8 characters long.";
  }

  if (!/[A-Z]/.test(password)) {
    return "Password must include at least 1 uppercase letter.";
  }

  if (!/[a-z]/.test(password)) {
    return "Password must include at least 1 lowercase letter.";
  }

  if (!/[0-9]/.test(password)) {
    return "Password must include at least 1 number.";
  }

  if (!/[^A-Za-z0-9]/.test(password)) {
    return "Password must include at least 1 special character.";
  }

  return null;
};

export const SetPasswordRoute = () => {
  const { uidb64, token } = useParams();
  const navigate = useNavigate();

  const [isValidating, setIsValidating] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const isUserValid = async () => {
    try {
      const response = await instance.get(
        `/api/setup-password/${uidb64}/${token}/`,
      );
      return response.status === 200;
    } catch (error) {
      console.error("Error validating invite: ", error);
      return false;
    }
  };

  const postPassword = useMutate<PasswordPostData>(
    `/api/setup-password/${uidb64}/${token}/`,
    "POST",
  );

  const handleSubmit = async (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const data: PasswordPostData = {
      new_password: formData.get("newPassword") as string,
      confirm_password: formData.get("confirmPassword") as string,
    };

    const passwordValidationError = validateNewPassword(data.new_password);
    if (passwordValidationError) {
      alert(passwordValidationError);
      setIsLoading(false);
      return;
    }

    if (data.new_password !== data.confirm_password) {
      alert("Passwords do not match!");
      setIsLoading(false);
      return;
    }

    try {
      const response = await postPassword.mutate(data);
      if (response.status === 200) {
        navigate("/login", { replace: true });
      }
    } catch (error) {
      console.error("Error setting password: ", error);
    }
  };

  useEffect(() => {
    const validateInviteUrl = async () => {
      if (!uidb64 || !token) {
        navigate("/login", { replace: true });
        return;
      }

      const valid = await isUserValid();
      if (!valid) {
        navigate("/login", { replace: true });
        return;
      }

      setIsValidating(false);
    };

    void validateInviteUrl();
  }, [navigate, token, uidb64]);

  // Do not render route UI until URL validation completes.
  if (isValidating) {
    return null;
  }

  return (
    <div className="cont">
      <div className="left-cont">
        <div className="center-img">
          <img
            className="login-img"
            src={"../../LoginScreenShot.png"}
            alt="Dashboard"
          />
          <p>
            A simplified gateway to effortless visibility and asset management.
          </p>
        </div>
      </div>
      <div className="right-cont">
        <div className="login-cont">
          <div className="logo-cont bottom-space">
            <Logo fillColor="#557AFF" />
            <h4>Forward</h4>
          </div>
          <h1 className="bottom-space">Set your password</h1>
          <form id="login-form" onSubmit={handleSubmit}>
            <div className="bottom-space"></div>
            <ul>
              <div>Password must have the following requirements:</div>
              <li>At least 8 characters</li>
              <li>At least 1 uppercase letter</li>
              <li>At least 1 lowercase letter</li>
              <li>At least 1 number</li>
              <li>At least 1 special character</li>
            </ul>
            <div className="">
              <Input
                name="newPassword"
                type="password"
                label="newPassword"
                placeholder="Enter new password"
              />
            </div>
            <div className="bottom-space">
              <Input
                name="confirmPassword"
                type="password"
                label="confirmPassword"
                placeholder="Confirm new password"
              />
            </div>

            {isLoading ? (
              <div className="loading-cont">
                <div className="lds-ellipsis">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </div>
            ) : (
              <button
                className="bottom-space change-pwd-btn"
                id="login-button"
                name="Sign in"
                data-testid="signin"
                type="submit"
              >
                Submit
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetPasswordRoute;
